import { getSupabaseBrowserClient } from '../lib/supabase'

export type RecordType = 'incident' | 'material_change'
export type CustomerStatus = 'reported' | 'under_review' | 'action_required' | 'closed'

export type ChangeIncidentListItem = {
  id: string
  recordType: RecordType
  reference: string
  title: string
  aiUseCaseId: string | null
  aiUseName: string
  classification: string
  status: CustomerStatus
  reportedAt: string
}

export type RequiredAction = { kind: string; label: string; reference: string; status: string; to: string | null }
export type ActivityItem = { eventType: string; occurredAt: string }

export type ChangeIncidentDetail = ChangeIncidentListItem & {
  description: string
  immediateAction?: string | null
  additionalContext?: string | null
  rawStatus: string
  occurredAt: string
  reportedBy: string
  outcome?: string | null
  reviewNotes?: string | null
  requiredActionSummary?: string | null
  reportedSeverity?: string | null
  assessmentAffected?: boolean | null
  security: boolean
  privacy: boolean
  legal: boolean
  reassessment: boolean | null
  reapproval: boolean | null
  canManage: boolean
  activity: ActivityItem[]
  requiredActions: RequiredAction[]
}

type RpcResponse = PromiseLike<{ data: unknown; error: { message: string } | null }>
type RpcInvoker = (name: string, args?: Record<string, unknown>) => RpcResponse
async function invoke(name: string, args: Record<string, unknown> = {}) {
  const client = getSupabaseBrowserClient() as unknown as { rpc: RpcInvoker }
  const { data, error } = await client.rpc(name, args)
  if (error) throw new Error(error.message)
  return data
}
function object(value: unknown): Record<string, unknown> { return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {} }
function rows(value: unknown): Record<string, unknown>[] { return Array.isArray(value) ? value.map(object) : [] }
function str(value: unknown): string { return typeof value === 'string' ? value : '' }
function maybe(value: unknown): string | null { return typeof value === 'string' && value ? value : null }
function bool(value: unknown): boolean { return value === true || value === 'true' }

function mapItem(row: Record<string, unknown>): ChangeIncidentListItem {
  return { id: str(row.id), recordType: str(row.recordType) as RecordType, reference: str(row.reference), title: str(row.title), aiUseCaseId: maybe(row.aiUseCaseId), aiUseName: str(row.aiUseName), classification: str(row.classification), status: str(row.status) as CustomerStatus, reportedAt: str(row.reportedAt) }
}

export async function listChangesIncidents(organisationId: string, aiUseCaseId?: string | null) {
  const result = object(await invoke('list_customer_changes_incidents', { p_organisation_id: organisationId, p_ai_use_case_id: aiUseCaseId || null }))
  return { items: rows(result.items).map(mapItem), canManage: bool(result.canManage) }
}

export async function getChangeIncidentDetail(recordType: RecordType, id: string): Promise<ChangeIncidentDetail> {
  const row = object(await invoke('get_customer_change_incident_detail', { p_record_type: recordType, p_record_id: id }))
  return {
    ...mapItem(row), description: str(row.description), immediateAction: maybe(row.immediateAction), additionalContext: maybe(row.additionalContext), rawStatus: str(row.rawStatus), occurredAt: str(row.occurredAt), reportedBy: str(row.reportedBy), outcome: maybe(row.outcome), reviewNotes: maybe(row.reviewNotes), requiredActionSummary: maybe(row.requiredActionSummary), reportedSeverity: maybe(row.reportedSeverity), assessmentAffected: typeof row.assessmentAffected === 'boolean' ? row.assessmentAffected : null,
    security: bool(row.security), privacy: bool(row.privacy), legal: bool(row.legal), reassessment: typeof row.reassessment === 'boolean' ? row.reassessment : null, reapproval: typeof row.reapproval === 'boolean' ? row.reapproval : null, canManage: bool(row.canManage),
    activity: rows(row.activity).map((item) => ({ eventType: str(item.eventType), occurredAt: str(item.occurredAt) })),
    requiredActions: rows(row.requiredActions).map((item) => ({ kind: str(item.kind), label: str(item.label), reference: str(item.reference), status: str(item.status), to: maybe(item.to) })),
  }
}

export async function reportIncident(input: { organisationId: string; aiUseCaseId?: string | null; description: string; occurredAt: string; severity: string; immediateAction?: string }) {
  return invoke('report_customer_incident', { p_organisation_id: input.organisationId, p_ai_use_case_id: input.aiUseCaseId || null, p_description: input.description, p_occurred_at: input.occurredAt, p_severity: input.severity, p_immediate_action: input.immediateAction || null })
}
export async function recordMaterialChange(input: { aiUseCaseId: string; description: string; occurredAt: string; changeCategory: string; additionalContext?: string }) {
  return invoke('record_customer_material_change', { p_ai_use_case_id: input.aiUseCaseId, p_description: input.description, p_occurred_at: input.occurredAt, p_change_category: input.changeCategory, p_additional_context: input.additionalContext || null })
}
export async function reviewIncident(input: { id: string; aiUseCaseId?: string | null; severity: string; security: boolean; privacy: boolean; legal: boolean; reassessment: boolean; reapproval: boolean; actions: string; notes: string }) {
  return invoke('review_customer_incident', { p_incident_id: input.id, p_ai_use_case_id: input.aiUseCaseId || null, p_confirmed_severity: input.severity, p_security: input.security, p_privacy: input.privacy, p_legal: input.legal, p_reassessment: input.reassessment, p_reapproval: input.reapproval, p_required_action_summary: input.actions, p_review_notes: input.notes || null })
}
export async function reviewMaterialChange(input: { id: string; assessmentAffected: boolean; security: boolean; privacy: boolean; legal: boolean; reassessment: boolean; reapproval: boolean; actions: string; notes: string }) {
  return invoke('review_customer_material_change', { p_material_change_id: input.id, p_assessment_affected: input.assessmentAffected, p_security: input.security, p_privacy: input.privacy, p_legal: input.legal, p_reassessment: input.reassessment, p_reapproval: input.reapproval, p_required_action_summary: input.actions, p_review_notes: input.notes || null })
}
export async function closeRecord(recordType: RecordType, id: string, outcome: string) {
  return invoke(recordType === 'incident' ? 'close_customer_incident' : 'close_customer_material_change', recordType === 'incident' ? { p_incident_id: id, p_outcome: outcome } : { p_material_change_id: id, p_outcome: outcome })
}
